// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Beeline — one round of a live race, decoupled from Trystero, the DOM and the
// clock (driven by guess()/tick(), which is what makes the host-transfer contract
// testable without a network — tests/takeover.test.ts).
//
// The shape is a PARALLEL SAME-SEED RACE: every peer holds its own copy of the
// identical mystery (a pure function of the frozen seed) and solves it
// independently. NO game state crosses the wire — only a tiny status gossip (guess
// count, solved, and a final guess trail for the results). Because each peer
// reaches its own game-over (solve / budget out / local timer) with no help from
// the host, HOST TRANSFER IS A PURE DISPLAY CONCERN: a promoted peer keeps
// relaying gossip and can still finish. onHostChange is wired regardless.

import {
  createGame,
  guessCountry,
  markTimeout,
  type GameState,
  type Guess,
} from './game';
import type { Mode } from './modes';

export interface GuessLite {
  id: string;
  km: number;
  pct: number;
  correct: boolean;
}

export interface PeerResult {
  seat: number;
  name: string;
  solved: boolean;
  over: boolean;
  guessCount: number;
  bestPct: number;
  /** The full guess trail — only populated once the peer finishes. */
  trail: GuessLite[];
  left: boolean;
}

/** Wire status. Tiny; the trail rides only on the final message. */
export interface RaceMsg {
  n: number;
  solved: boolean;
  done: boolean;
  best: number;
  trail?: Array<[string, number, number]>;
}

export interface RaceDeps {
  send: (m: RaceMsg) => void;
  onChange: () => void;
  onSelfEnd: (g: GameState) => void;
}

export interface RaceOpts {
  mode: Mode;
  seed: number;
  /** Roster seat of this peer, or null for a spectator. */
  selfSeat: number | null;
  names: string[];
  isHost: boolean;
  deps: RaceDeps;
}

export class Race {
  state: GameState;
  readonly selfSeat: number | null;
  readonly mode: Mode;
  private host: boolean;
  private readonly deps: RaceDeps;
  private timeLeft: number;
  private ended = false;
  readonly results = new Map<number, PeerResult>();

  constructor(o: RaceOpts) {
    this.mode = o.mode;
    this.state = createGame(o.mode, o.seed);
    this.selfSeat = o.selfSeat;
    this.host = o.isHost;
    this.deps = o.deps;
    this.timeLeft = o.mode.timeMs;
    o.names.forEach((name, seat) => {
      this.results.set(seat, {
        seat,
        name,
        solved: false,
        over: false,
        guessCount: 0,
        bestPct: 0,
        trail: [],
        left: false,
      });
    });
    if (o.selfSeat !== null) this.syncSelf();
  }

  get isHost(): boolean {
    return this.host;
  }

  get msLeft(): number {
    return Math.max(0, this.timeLeft);
  }

  private self(): PeerResult | undefined {
    return this.selfSeat === null ? undefined : this.results.get(this.selfSeat);
  }

  private syncSelf(): void {
    const r = this.self();
    if (!r) return;
    r.guessCount = this.state.guesses.length;
    r.solved = this.state.solved;
    r.over = this.state.over;
    r.bestPct = this.state.guesses.reduce((m, g) => Math.max(m, g.pct), 0);
    if (this.state.over) r.trail = this.state.guesses.map(lite);
  }

  /** Play a local guess; returns whether it changed anything. */
  guess(id: string): boolean {
    if (this.selfSeat === null || this.state.over) return false;
    const next = guessCountry(this.state, id);
    if (next === this.state) return false; // rejected: dupe / invalid / not in pool
    this.state = next;
    this.syncSelf();
    if (this.state.over) return this.finishSelf();
    this.broadcastProgress();
    this.deps.onChange();
    return true;
  }

  private broadcastProgress(): void {
    const r = this.self();
    if (!r) return;
    this.deps.send({ n: r.guessCount, solved: r.solved, done: false, best: r.bestPct });
  }

  private finishSelf(): boolean {
    if (this.ended) return true;
    this.ended = true;
    this.syncSelf();
    const r = this.self();
    if (r) {
      this.deps.send({
        n: r.guessCount,
        solved: r.solved,
        done: true,
        best: r.bestPct,
        trail: r.trail.map((g) => [g.id, g.km, g.pct] as [string, number, number]),
      });
    }
    this.deps.onChange();
    this.deps.onSelfEnd(this.state);
    return true;
  }

  /** A status update from another peer. */
  receive(msg: RaceMsg, fromSeat: number): void {
    const r = this.results.get(fromSeat);
    if (!r || r.seat === this.selfSeat) return;
    r.guessCount = msg.n;
    r.solved = msg.solved;
    r.over = msg.done || r.over;
    r.bestPct = Math.max(r.bestPct, msg.best);
    if (msg.trail) {
      r.trail = msg.trail.map(([id, km, pct]) => ({ id, km, pct, correct: pct >= 100 }));
      r.over = true;
    }
    this.deps.onChange();
  }

  /**
   * Drive the local clock. Interval-driven, never rAF alone (a backgrounded tab
   * would freeze the timer). Independent of host status — every seated peer runs
   * its own timer, so the round always ends.
   */
  tick(dtMs: number): void {
    if (this.selfSeat === null || this.state.over) return;
    this.timeLeft -= dtMs;
    if (this.timeLeft <= 0) {
      this.state = markTimeout(this.state);
      this.syncSelf();
      this.finishSelf();
    }
  }

  /** Promotion carries no authoritative duty here — recorded for display only. */
  setHost(isHost: boolean): void {
    this.host = isHost;
  }

  peerLeft(seat: number): void {
    const r = this.results.get(seat);
    if (!r || seat === this.selfSeat) return;
    r.left = true;
    r.over = true; // a departed peer will never finish; don't hold the room for it
    this.deps.onChange();
  }

  peerReturned(seat: number): void {
    const r = this.results.get(seat);
    if (r) r.left = false;
  }

  /** True once every seat has finished (or left). */
  everyoneDone(): boolean {
    return [...this.results.values()].every((r) => r.over);
  }

  /** Results best-first: solved (fewest guesses) → unsolved (warmest) → left. */
  ranked(): PeerResult[] {
    return [...this.results.values()].sort((a, b) => {
      if (a.left !== b.left) return a.left ? 1 : -1;
      if (a.solved !== b.solved) return a.solved ? -1 : 1;
      if (a.solved && b.solved) return a.guessCount - b.guessCount;
      return b.bestPct - a.bestPct;
    });
  }
}

function lite(g: Guess): GuessLite {
  return { id: g.id, km: g.km, pct: g.pct, correct: g.correct };
}

/** Seat index from the frozen roster, or null for a spectator / unknown peer. */
export function seatOf(roster: Array<{ id: string }>, peerId: string): number | null {
  const i = roster.findIndex((p) => p.id === peerId);
  return i < 0 ? null : i;
}

/**
 * A one-shot timer that is safe to poke repeatedly — re-arming on every poll tick
 * starves the timer, a real shipped-in-development hang.
 */
export function createOneShot(delayMs: number, run: () => void) {
  let id: ReturnType<typeof setTimeout> | null = null;
  return {
    poke(): void {
      if (id !== null) return;
      id = setTimeout(() => {
        id = null;
        run();
      }, delayMs);
    },
    cancel(): void {
      if (id !== null) clearTimeout(id);
      id = null;
    },
  };
}
